// PII detection service — calls Kaycoo PII agent for redaction
import { executeAgent } from '@/modules/agents/agent-client'
import { getAgentForFeature } from '@/hooks/useOrgSettings'
import type { PIIDetectionResult } from '@/types/pii'

const PII_AGENT_ID = 'cae43ba9-b28e-4098-947d-3e088385f88b'

function getAgentId(): string {
  return getAgentForFeature(
    'pii_redaction',
    import.meta.env.VITE_KAYCOO_PII_REDACTION_AGENT_ID || PII_AGENT_ID,
  )
}

/**
 * Detect PII in text using the local Ollama-based Kaycoo agent.
 * Data never leaves the Railway infrastructure.
 */
export async function detectPII(text: string): Promise<PIIDetectionResult> {
  if (!text.trim()) {
    return { pii_found: [], total_found: 0, has_critical: false, redacted_text: text }
  }

  // Truncate very large texts to avoid overwhelming the model
  const maxLength = 15000
  const truncated = text.length > maxLength ? text.slice(0, maxLength) + '\n[... truncated]' : text

  const agentId = getAgentId()
  const result = await executeAgent(agentId, { input: truncated })
  const responseText = result.response || result.result || result.output || ''

  return parsePIIResponse(responseText, text)
}

/**
 * Apply redaction decisions to original text.
 * Replaces PII instances from end to start to preserve positions.
 */
export function applyRedactions(
  originalText: string,
  decisions: Array<{ decision: 'redact' | 'keep'; item: { original: string; redacted: string } }>,
): string {
  let result = originalText
  // Process redactions — replace all instances of each PII value
  for (const { decision, item } of decisions) {
    if (decision === 'redact') {
      // Replace all occurrences of the original text
      const escaped = item.original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      result = result.replace(new RegExp(escaped, 'g'), item.redacted)
    }
  }
  return result
}

function parsePIIResponse(responseText: string, originalText: string): PIIDetectionResult {
  const empty: PIIDetectionResult = {
    pii_found: [], total_found: 0, has_critical: false, redacted_text: originalText,
  }

  try {
    // Try fenced JSON
    const fenced = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```/)
    if (fenced) return validateResult(JSON.parse(fenced[1]), originalText)

    // Try raw JSON
    const raw = responseText.match(/\{[\s\S]*"pii_found"[\s\S]*\}/)
    if (raw) return validateResult(JSON.parse(raw[0]), originalText)

    // Try the whole response as JSON
    const parsed = JSON.parse(responseText)
    if (parsed.pii_found) return validateResult(parsed, originalText)
  } catch {
    console.warn('Failed to parse PII response, treating as no PII found')
  }

  return empty
}

function validateResult(data: Record<string, unknown>, originalText: string): PIIDetectionResult {
  const piiFound = Array.isArray(data.pii_found) ? data.pii_found : []
  const validItems = piiFound.filter((item: Record<string, unknown>) =>
    typeof item.type === 'string' &&
    typeof item.original === 'string' &&
    typeof item.redacted === 'string'
  )

  return {
    pii_found: validItems,
    total_found: validItems.length,
    has_critical: Boolean(data.has_critical),
    redacted_text: typeof data.redacted_text === 'string' ? data.redacted_text : originalText,
  }
}
